import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';

import { PhoneForm } from '@/components/Forms/PhoneForm/PhoneForm.loadable';
import { getE164FormattedNumber, getNationalPhoneNumberForm, PhoneFormShape } from '@/components/Forms/utils/phoneForm';
import FormInput from '@/components/Input/FormInput';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppSelector } from '@/redux/hooks';
import { getBidderPhoneNumber } from '@/redux/modules/account/user/user.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { email as validateEmail, isNameTooLong } from '@/utils/validation';

type FieldErrors = {
    email: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
};

type FieldValid = {
    email: boolean;
    firstName: boolean;
    lastName: boolean;
    mobileNumber: boolean;
};

export type ProfileBasicsData = {
    email: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
};

type ProfileBasicsMeta = {
    fieldErrors: FieldErrors;
    fieldValid: FieldValid;
};

export type ProfileBasicsFormShape = ProfileBasicsData & ProfileBasicsMeta;

type Props = {
    readonly className?: string;
    readonly formData: ProfileBasicsData;
    readonly mobileVerified?: boolean;
    readonly updateParentForm: (formValid: boolean, formBasics: ProfileBasicsData) => void;
};

export const ProfileBasicsForm = ({ className, formData, mobileVerified, updateParentForm }: Props) => {
    const basicsForm = useRef<ProfileBasicsFormShape>({
        email: formData.email,
        fieldErrors: {
            email: 'formErrors.none',
            firstName: 'formErrors.none',
            lastName: 'formErrors.none',
            mobileNumber: 'formErrors.none',
        },
        fieldValid: {
            email: true,
            firstName: true,
            lastName: true,
            mobileNumber: true,
        },
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNumber: formData.mobileNumber,
    });

    const formatMessage = useAppFormatMessage();
    const phoneNumberForm = getNationalPhoneNumberForm(basicsForm.current.mobileNumber);
    const [shouldUpdateParentForm, setShouldUpdateParentForm] = useState(false);
    const [updatedPhone, setUpdatedPhone] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState<PhoneFormShape>(phoneNumberForm);
    const accountPhoneNumber = useAppSelector((state) => getBidderPhoneNumber(state));
    const prevForm = usePrevious(formData);
    const isVerified = mobileVerified && accountPhoneNumber === basicsForm.current.mobileNumber;

    useEffect(() => {
        if (!isEqual(formData, prevForm)) {
            basicsForm.current = {
                ...basicsForm.current,
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                mobileNumber: formData.mobileNumber,
            };
        }
    }, [formData, prevForm]);

    useEffect(() => {
        if (!shouldUpdateParentForm) {
            return;
        }
        const formValid =
            basicsForm.current.fieldValid.email &&
            basicsForm.current.fieldValid.firstName &&
            basicsForm.current.fieldValid.lastName &&
            basicsForm.current.fieldValid.mobileNumber;

        updateParentForm(formValid, {
            email: basicsForm.current.email,
            firstName: basicsForm.current.firstName,
            lastName: basicsForm.current.lastName,
            mobileNumber: basicsForm.current.mobileNumber,
        } as ProfileBasicsData);
        setShouldUpdateParentForm(false);
    }, [updateParentForm, shouldUpdateParentForm]);

    const handlePhoneFormChange = useCallback((phoneForm: PhoneFormShape, isValid: boolean) => {
        const pnum = getE164FormattedNumber(phoneForm).substring(1);

        if (!isValid) {
            basicsForm.current.fieldErrors.mobileNumber = 'formErrors.mobileNumberInvalid';
            basicsForm.current.fieldValid.mobileNumber = false;
        } else {
            basicsForm.current.fieldErrors.mobileNumber = 'formErrors.none';
            basicsForm.current.fieldValid.mobileNumber = true;
        }
        basicsForm.current.mobileNumber = pnum;

        setShouldUpdateParentForm(true);
        setPhoneNumber(phoneForm);
        setUpdatedPhone(true);
    }, []);

    const validateForm = useCallback((fieldName: string, value: string) => {
        const { fieldErrors, fieldValid } = basicsForm.current;
        let valid = true;
        let errorMessage = '';

        if (fieldName === 'email') {
            if (value === '' || !validateEmail(value)) {
                valid = false;
                errorMessage = 'formErrors.emailEmpty';
            }
        } else if (fieldName === 'firstName' && isNameTooLong(value)) {
            valid = false;
            errorMessage = 'formErrors.firstNameTooLong';
        } else if (fieldName === 'lastName' && isNameTooLong(value)) {
            valid = false;
            errorMessage = 'formErrors.lastNameTooLong';
        }

        const updatedFieldsValue = {
            ...fieldValid,
            [fieldName]: valid,
        };

        const updatedFieldErrors = {
            ...fieldErrors,
            [fieldName]: errorMessage,
        };

        basicsForm.current = {
            ...basicsForm.current,
            fieldErrors: updatedFieldErrors,
            [fieldName]: value,
            fieldValid: updatedFieldsValue,
        };
    }, []);

    const handleFormChange = useCallback(
        (event: { target: any }) => {
            setShouldUpdateParentForm(true);
            validateForm(event.target.id, event.target.value);
        },
        [validateForm]
    );

    const rowClassName = cn('mb-lg flex justify-between xsMax:mb-md');

    return (
        <section className={className}>
            <div className={cn('gap-sm', rowClassName)}>
                <FormInput
                    autoComplete="given-name"
                    className="w-1/2"
                    form={basicsForm.current}
                    id="firstName"
                    onChange={handleFormChange}
                    data-testid="accountSettingsFirstName"
                    value={basicsForm.current.firstName || ''}
                />
                <FormInput
                    autoComplete="family-name"
                    className="w-1/2"
                    form={basicsForm.current}
                    id="lastName"
                    onChange={handleFormChange}
                    data-testid="accountSettingsLastName"
                    value={basicsForm.current.lastName || ''}
                />
            </div>
            <div className={rowClassName}>
                <FormInput
                    autoComplete="email"
                    className="w-full"
                    form={basicsForm.current}
                    id="email"
                    onChange={handleFormChange}
                    data-testid="accountSettingsEmailInput"
                    value={basicsForm.current.email || ''}
                />
            </div>
            <div className={rowClassName}>
                <PhoneForm
                    className="smMax:w-full smMax:max-w-full"
                    errorMessage={String(formatMessage(['formErrors', 'mobileNumberInvalid']))}
                    onChange={handlePhoneFormChange}
                    phoneLabel={formatMessage(['completeAccountModal', 'mobilePhone'])}
                    phoneNumber={phoneNumber}
                    submitted={updatedPhone}
                    testid="accountSettingsMobile"
                    verified={isVerified}
                />
            </div>
        </section>
    );
};
